import type { AxiosError } from 'axios';
import type { ReactNode } from 'react';
import {
  Box,
  Button,
  Center,
  Container,
  Group,
  Loader,
  Menu,
  Stack,
  Text,
  Title
} from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import type { Offer, OfferLine, PriceListItem } from '@/api/generated/models';
import type { DraftSummary, DraftValue } from '@/utils/useItemsDraft';
import { useListContracts } from '@/api/generated/contracts/contracts';
import { OfferStatus } from '@/api/generated/models/offerStatus';
import {
  getGetOfferQueryKey,
  getListOfferLinesQueryKey,
  getListOffersQueryKey,
  useAcceptOffer,
  useApproveOffer,
  useCancelOffer,
  useDeactivateOffer,
  useGetOffer,
  useListOfferLines,
  useRejectOffer,
  useReturnOfferToDraft,
  useSaveOfferLines,
  useSendOffer
} from '@/api/generated/offers/offers';
import { useListPriceLists } from '@/api/generated/price-lists/price-lists';
import { useListUnits } from '@/api/generated/units/units';
import { IconFigmaInfo, IconFigmaXCircle } from '@/assets/icons';
import {
  BackButton,
  CardSection,
  ConfirmActionModal,
  DRAFT_ROW_BACKGROUND,
  DraftNumberInput,
  ItemsSaveBar,
  ItemsSaveStatus,
  KebabTrigger,
  LinkValue,
  ListPagination,
  MobileActionBar,
  MutationButton,
  NewItemBadge,
  StatusBanner,
  StatusPill,
  StatusTracker,
  TABLE_ITEM_CODE_COL_WIDTH,
  tableRowStyle,
  Toast,
  UndoRemoveButton,
  useUnsavedChangesGuard
} from '@/components/common';
import { useAuthContext } from '@/features/auth/context/AuthContext';
import { OfferItemPickerModal } from '@/features/offers/components/OfferItemPickerModal';
import { OfferLineFormModal } from '@/features/offers/components/OfferLineFormModal';
import { RejectOfferModal } from '@/features/offers/components/RejectOfferModal';
import { SendOfferModal } from '@/features/offers/components/SendOfferModal';
import { buildOfferTrackerSteps, statusConfig } from '@/features/offers/offerDisplay';
import { findUnitLabel } from '@/features/units/unitDisplay';
import { formatAmount, formatDate, formatNumber, otherPartyName } from '@/utils/format';
import { openBinaryInNewTab } from '@/utils/openBinaryInNewTab';
import { useItemsDraft } from '@/utils/useItemsDraft';
import { useLocalPagination } from '@/utils/useLocalPagination';
import { useSavedMessage } from '@/utils/useSavedMessage';

const divider = <div style={{ height: 1, backgroundColor: 'var(--color-border-soft)' }} />;

/** Uklanjanje stavke u nacrtu — stavka se briše tek kad se spreme stavke. */
const RemoveLineButton = ({ onClick }: { onClick: () => void }) => (
  <Box
    role="button"
    tabIndex={0}
    onClick={onClick}
    onKeyDown={(event) => {
      if (event.key === 'Enter' || event.key === ' ') onClick();
    }}
    style={{ cursor: 'pointer', color: 'var(--color-status-danger)' }}
  >
    <IconFigmaXCircle size={16} />
  </Box>
);

/** Za ad-hoc stavke (bez cjenika) — itemCode/category/description/unit/normHours
 * se mogu urediti (za razliku od stavki iz cjenika, koje se mogu samo ukloniti). */
const LineActionsMenu = ({ onEdit, onRemove }: { onEdit: () => void; onRemove: () => void }) => {
  const { t } = useTranslation();
  return (
    <Menu position="bottom-end" withinPortal shadow="md" radius="md" width={200}>
      <Menu.Target>
        <KebabTrigger />
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item styles={{ item: { color: 'var(--color-text-primary)' } }} onClick={onEdit}>
          {t('common.edit')}
        </Menu.Item>
        <Menu.Item styles={{ item: { color: 'var(--color-status-danger)' } }} onClick={onRemove}>
          {t('common.delete')}
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
};

/** Redak tablice stavki: spremljena stavka s primijenjenim nacrtom ili nova stavka iz nacrta. */
interface DisplayLine {
  key: string;
  lineId?: string;
  tempId?: string;
  line?: OfferLine;
  isNew: boolean;
  isRemoved: boolean;
  itemCode?: string;
  description?: string;
  category?: string;
  unitId?: string;
  normHours?: number;
  quantity: DraftValue;
  unitPrice: DraftValue;
  savedQuantity?: number;
  savedUnitPrice?: number;
  isChanged: boolean;
  quantityError: string | null;
  priceError: string | null;
}

export const OfferDetailPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuthContext();
  const queryClient = useQueryClient();

  // refetchOnMount: 'always' — status se mijenja od druge strane (klijent
  // prihvaća/odbija), pa ne smijemo prikazati zastarjeli status iz keša kad
  // se otvori detalj s liste.
  const { data, isLoading } = useGetOffer(id ?? '', { query: { refetchOnMount: 'always' } });
  const offer = data as Offer | undefined;

  const { data: linesData } = useListOfferLines(id ?? '', {
    query: { refetchOnMount: 'always' }
  });
  const lines: OfferLine[] = linesData ?? [];
  const { data: unitsData } = useListUnits();

  // Stavke se ne spremaju same — izmjene, nove i uklonjene stavke čekaju u nacrtu dok korisnik
  // ne klikne "Spremi stavke" (jedan zahtjev, jedna transakcija na backendu).
  const draft = useItemsDraft<PriceListItem>();
  const [justSaved, setJustSaved] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const nonNegativeError = t('itemsDraft.errorNonNegative');

  const displayLines: DisplayLine[] = [
    ...lines.map((line): DisplayLine => {
      const lineId = line.id ?? '';
      const savedQuantity = line.quantity ?? 0;
      const savedUnitPrice = line.unitPrice ?? 0;
      const quantity = draft.valueOf(lineId, 'quantity', savedQuantity);
      const unitPrice = draft.valueOf(lineId, 'unitPrice', savedUnitPrice);
      const isRemoved = draft.isRemoved(lineId);
      return {
        key: lineId,
        lineId,
        line,
        isNew: false,
        isRemoved,
        itemCode: line.itemCode,
        description: line.description,
        category: line.category,
        unitId: line.unitId,
        normHours: line.normHours,
        quantity,
        unitPrice,
        savedQuantity,
        savedUnitPrice,
        isChanged: !isRemoved && (quantity !== savedQuantity || unitPrice !== savedUnitPrice),
        quantityError: !isRemoved && quantity === '' ? nonNegativeError : null,
        priceError: !isRemoved && unitPrice === '' ? nonNegativeError : null
      };
    }),
    ...draft.added.map((item): DisplayLine => ({
      key: item.tempId,
      tempId: item.tempId,
      isNew: true,
      isRemoved: false,
      itemCode: item.meta.itemCode,
      description: item.meta.description,
      category: item.meta.category,
      unitId: item.meta.unitId,
      normHours: item.meta.normHours,
      quantity: item.values.quantity ?? 0,
      unitPrice: item.values.unitPrice ?? 0,
      // Početne vrijednosti nove stavke — polje se označi tek kad ga korisnik promijeni.
      savedQuantity: 0,
      savedUnitPrice: item.meta.unitPrice ?? 0,
      isChanged: false,
      quantityError: item.values.quantity === '' ? nonNegativeError : null,
      priceError: item.values.unitPrice === '' ? nonNegativeError : null
    }))
  ];
  const lineAmount = (line: DisplayLine) =>
    (Number(line.quantity) || 0) * (Number(line.unitPrice) || 0);
  const changedCount = displayLines.filter((line) => line.isChanged).length;
  const removedCount = displayLines.filter((line) => line.isRemoved).length;
  const draftSummary: DraftSummary = {
    changed: changedCount,
    added: draft.added.length,
    removed: removedCount,
    errors: displayLines.filter((line) => line.quantityError || line.priceError).length,
    total: changedCount + draft.added.length + removedCount
  };

  const setLineValue = (line: DisplayLine, field: 'quantity' | 'unitPrice', value: DraftValue) => {
    setJustSaved(false);
    if (line.tempId) draft.setAddedValue(line.tempId, field, value);
    else if (line.lineId) draft.setValue(line.lineId, field, value);
  };
  const removeLine = (line: DisplayLine) => {
    setJustSaved(false);
    if (line.tempId) draft.discardAdded(line.tempId);
    else if (line.lineId) draft.toggleRemoved(line.lineId);
  };

  const {
    page: linesPage,
    setPage: setLinesPage,
    pageSize: linesPageSize,
    setPageSize: setLinesPageSize,
    totalPages: linesTotalPages,
    pageItems: pageLines,
    offset: linesOffset
  } = useLocalPagination(displayLines);

  const saveLinesMutation = useSaveOfferLines({
    mutation: { meta: { skipGlobalErrorToast: true } }
  });

  const saveDraft = async (): Promise<boolean> => {
    if (!id || draftSummary.errors > 0) return false;
    setSaveError(null);
    try {
      await saveLinesMutation.mutateAsync({
        id,
        data: {
          removedLineIds: displayLines
            .filter((line) => line.isRemoved && line.lineId)
            .map((line) => line.lineId as string),
          updatedLines: displayLines
            .filter((line) => line.isChanged && line.lineId)
            .map((line) => ({
              lineId: line.lineId as string,
              quantity: Number(line.quantity),
              unitPrice: Number(line.unitPrice)
            })),
          addedLines: draft.added
            .filter((item) => item.meta.id)
            .map((item) => ({
              priceListItemId: item.meta.id as string,
              quantity: Number(item.values.quantity),
              unitPrice: Number(item.values.unitPrice)
            }))
        }
      });
      // Nacrt se briše tek kad su svježe stavke stigle — inače bi na trenutak bljesnulo staro stanje.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: getListOfferLinesQueryKey(id) }),
        queryClient.invalidateQueries({ queryKey: getGetOfferQueryKey(id) }),
        queryClient.invalidateQueries({ queryKey: getListOffersQueryKey() })
      ]);
      draft.reset();
      setJustSaved(true);
      setSavedAt(
        new Date().toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' })
      );
      return true;
    } catch (error) {
      const backendMessage = (error as AxiosError<{ error?: string }>)?.response?.data?.error;
      setSaveError(
        t('itemsDraft.saveFailed', {
          reason: typeof backendMessage === 'string' ? backendMessage : t('common.error')
        })
      );
      return false;
    }
  };

  const discardDraft = () => {
    draft.reset();
    setSaveError(null);
  };

  const { guard, modal: unsavedChangesModal } = useUnsavedChangesGuard({
    summary: draftSummary,
    onSave: saveDraft,
    onDiscard: discardDraft
  });

  const addPriceListItems = (items: PriceListItem[]) => {
    setJustSaved(false);
    draft.addItems(
      items.map((item) => ({
        meta: item,
        values: { quantity: 0, unitPrice: item.unitPrice ?? 0 }
      }))
    );
  };

  // Samo za rezoluciju je li ugovor već napravljen iz ove ponude — vidi
  // linkedContract niže (Contract.offerId je jedini FK, ponuda ga ne drži).
  const { data: contractsData } = useListContracts();
  const linkedContract = (contractsData ?? []).find((c) => c.offerId === offer?.id);

  // objectCode/objectName su slobodan tekst upisan izravno na ponudi (vidi
  // CreateOfferRequest) — nema stvarnog FK-a na objects(id), pa se ne
  // razrješavaju preko useListObjects niti vode na registar objekata.
  //
  // Offer ne drži priceListName (samo id) — razrješavamo ga ovdje za "Cjenik"
  // link u zaglavlju. Na klijentovoj kopiji ponude je namjerno null
  // (partnerov FK, vidi OfferService.send()), pa se link jednostavno ne prikazuje.
  const { data: priceListsData } = useListPriceLists();
  const linkedPriceList = (priceListsData ?? []).find((pl) => pl.id === offer?.priceListId);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: getGetOfferQueryKey(id) });
    queryClient.invalidateQueries({ queryKey: getListOffersQueryKey() });
  };

  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [addItemModalOpen, setAddItemModalOpen] = useState(false);
  const [editingLine, setEditingLine] = useState<OfferLine | null>(null);
  const [savedMessage, setSavedMessage] = useSavedMessage();
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(false);

  const handlePreview = async () => {
    if (!id) return;
    setPreviewError(false);
    setIsPreviewLoading(true);
    try {
      await openBinaryInNewTab(`/api/v1/offers/${id}/pdf`);
    } catch {
      setPreviewError(true);
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const openEditLineModal = (line: OfferLine) => {
    setEditingLine(line);
    setAddItemModalOpen(true);
  };

  const closeLineModal = () => {
    setAddItemModalOpen(false);
    setEditingLine(null);
  };

  const acceptMutation = useAcceptOffer({ mutation: { onSuccess: invalidate } });
  const rejectMutation = useRejectOffer({
    mutation: {
      onSuccess: () => {
        invalidate();
        setRejectModalOpen(false);
      }
    }
  });
  const sendMutation = useSendOffer({
    mutation: {
      onSuccess: () => {
        invalidate();
        setSendModalOpen(false);
      }
    }
  });
  const approveMutation = useApproveOffer({
    mutation: {
      onSuccess: () => {
        invalidate();
      }
    }
  });
  const returnToDraftMutation = useReturnOfferToDraft({
    mutation: {
      onSuccess: () => {
        invalidate();
      }
    }
  });
  const cancelMutation = useCancelOffer({
    mutation: {
      onSuccess: () => {
        invalidate();
        setCancelModalOpen(false);
      }
    }
  });
  const deactivateMutation = useDeactivateOffer({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListOffersQueryKey() });
        navigate('/offers');
      },
      onError: (error) => {
        const backendMessage = (error as AxiosError<{ error?: string }>)?.response?.data?.error;
        setDeleteError(typeof backendMessage === 'string' ? backendMessage : t('common.error'));
        setDeleteModalOpen(false);
      }
    }
  });

  if (isLoading || !offer) {
    return (
      <Container size="lg" px={0}>
        <Center py={80}>
          <Loader size="sm" />
        </Center>
      </Container>
    );
  }

  const config = statusConfig[offer.status ?? OfferStatus.DRAFT];
  const isPending = offer.status === OfferStatus.SENT;
  const isAccepted = offer.status === OfferStatus.ACCEPTED;
  const isRejected = offer.status === OfferStatus.REJECTED;
  const canAct = isPending && !!user && user.organisationId === offer.clientOrgId;
  const canEdit =
    (offer.status === OfferStatus.DRAFT || offer.status === OfferStatus.REJECTED) &&
    !!user &&
    user.organisationId === offer.partnerOrgId;
  // OFF-NEW: Interno odobrenje je obavezan međukorak prije slanja — canEdit (DRAFT/REJECTED)
  // ostaje gate za uređivanje/dodavanje stavki, ali "Pošalji ponudu" se odvaja u zaseban canSend
  // koji traži APPROVED (vidi OfferService.send()).
  const canApprove = canEdit;
  const canSend =
    offer.status === OfferStatus.APPROVED && !!user && user.organisationId === offer.partnerOrgId;
  // OFF-NEW: jedini izlaz iz APPROVED natrag u DRAFT, ako je prije slanja uočena greška.
  const canReturnToDraft = canSend;
  const isPartnerView = !!user && user.organisationId === offer.partnerOrgId;
  const canDelete = offer.status === OfferStatus.DRAFT && isPartnerView;
  // OFF-NEW: Cancel je za ponudu od koje se odustalo — izvan DRAFT-a (ima Delete) i izvan
  // terminalnih statusa (ACCEPTED/REJECTED/EXPIRED/CANCELLED su već završni, vidi OfferService.cancel()).
  const canCancel =
    (offer.status === OfferStatus.APPROVED || offer.status === OfferStatus.SENT) && isPartnerView;
  // CON-09: klijent smije okinuti kreiranje ugovora iz svoje prihvaćene kopije, ne samo partner.
  const canCreateContract =
    isAccepted &&
    !!user &&
    (user.organisationId === offer.partnerOrgId || user.organisationId === offer.clientOrgId);
  const trackerSteps = buildOfferTrackerSteps(offer.status ?? OfferStatus.DRAFT, isPartnerView);
  const offerLabel = offer.offerNumber ?? offer.referenceId;
  const hasPriceList = !!offer.priceListId;
  const existingCategories = Array.from(
    new Set(lines.map((line) => line.category).filter(Boolean))
  ) as string[];

  // Ukupno prati nacrt — uključuje nespremljene promjene (napomena ispod iznosa).
  const subtotal = displayLines
    .filter((line) => !line.isRemoved)
    .reduce((sum, line) => sum + lineAmount(line), 0);
  // OFF-NEW-48: PDV samo ako je ponuda s PDV-om I stopa > 0 — inače se PDV redak ne prikazuje.
  const vatRate = offer.vatRate ?? 0;
  const showVat = !!offer.vatEnabled && vatRate > 0;
  const vat = showVat ? (subtotal * vatRate) / 100 : 0;
  const total = subtotal + vat;

  const objectValue =
    offer.objectCode || offer.objectName
      ? `${offer.objectCode ?? '—'} — ${offer.objectName ?? '—'}`
      : '—';
  const priceListValue = linkedPriceList ? (
    <LinkValue
      value={linkedPriceList.name ?? '—'}
      onClick={() => navigate(`/templates/price-lists/${linkedPriceList.id}`)}
    />
  ) : (
    '—'
  );
  const paymentTermsValue = offer.paymentTerms
    ? t('offerDetail.paymentTermsValue', { days: offer.paymentTerms })
    : '—';

  // Isti obrazac kao ContractDetailPage.infoField/acceptedTrailing — raspored polja mora biti
  // uniforman između Ponude i Ugovora (lijevo = identitet/reference, desno = datumi/iznos).
  const infoField = (label: string, value: ReactNode) => (
    <Stack gap={2} key={label}>
      <Text fz={11} fw={600} style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4 }}>
        {label}
      </Text>
      {typeof value === 'string' ? (
        <Text fz={13.5} style={{ color: 'var(--color-text-primary)' }}>
          {value}
        </Text>
      ) : (
        value
      )}
    </Stack>
  );

  const infoColumnDivider = (
    <div style={{ width: 1, alignSelf: 'stretch', backgroundColor: 'var(--color-border-soft)' }} />
  );

  const acceptedTrailing = isAccepted && (
    <Text fz={11} style={{ color: 'var(--color-text-muted)' }}>
      {t('offerDetail.acceptedBy')}{' '}
      <Text span fz={11} fw={600} style={{ color: 'var(--color-text-secondary)' }}>
        {offer.decidedByName ?? '—'}
      </Text>
      {'  ·  '}
      {t('offerDetail.acceptedDate')}{' '}
      <Text span fz={11} fw={600} style={{ color: 'var(--color-text-secondary)' }}>
        {formatDate(offer.updatedAt)}
      </Text>
    </Text>
  );

  const generalInfoContent = (
    <>
      <Box visibleFrom="sm" style={{ width: '100%' }}>
        <Stack
          gap={16}
          p={20}
          style={{
            borderRadius: 12,
            border: '1px solid var(--color-border-default)',
            backgroundColor: 'var(--color-bg-surface)'
          }}
        >
          <Text fz={14} fw={700} style={{ color: 'var(--color-text-primary)' }}>
            {t('offerDetail.generalInfo')}
          </Text>
          <Group gap={24} align="flex-start" wrap="nowrap" style={{ width: '100%' }}>
            <Stack gap={14} style={{ flex: '1 1 0', minWidth: 0 }}>
              {infoField(t('offerDetail.partner'), otherPartyName(offer, user?.orgName))}
              {infoField(t('offerDetail.inquiryReference'), offer.inquiryReference || '—')}
              {infoField(t('offerDetail.object'), objectValue)}
              {infoField(t('offerDetail.priceList'), priceListValue)}
            </Stack>

            {infoColumnDivider}

            <Stack gap={14} style={{ flex: '1 1 0', minWidth: 0 }}>
              {infoField(t('offerDetail.offerDate'), formatDate(offer.offerDate))}
              {infoField(t('offerDetail.validUntil'), formatDate(offer.validUntil))}
              {infoField(t('offerDetail.paymentTerms'), paymentTermsValue)}
              {infoField(
                t('offerDetail.totalValue'),
                <Text fz={13.5} fw={700} style={{ color: 'var(--color-text-primary)' }}>
                  {formatAmount(offer.totalValue)}
                </Text>
              )}
            </Stack>
          </Group>

          {acceptedTrailing && (
            <>
              <div style={{ height: 1, backgroundColor: 'var(--color-border-soft)' }} />
              {acceptedTrailing}
            </>
          )}
        </Stack>
      </Box>

      <Stack hiddenFrom="sm" gap={16} w="100%">
        <Stack
          gap={14}
          p={16}
          style={{
            borderRadius: 12,
            border: '1px solid var(--color-border-default)',
            backgroundColor: 'var(--color-bg-surface)'
          }}
        >
          <Text fz={14} fw={700} style={{ color: 'var(--color-text-primary)' }}>
            {t('offerDetail.generalInfo')}
          </Text>
          {infoField(t('offerDetail.partner'), otherPartyName(offer, user?.orgName))}
          {infoField(t('offerDetail.inquiryReference'), offer.inquiryReference || '—')}
          {infoField(t('offerDetail.object'), objectValue)}
          {infoField(t('offerDetail.priceList'), priceListValue)}
          {infoField(t('offerDetail.offerDate'), formatDate(offer.offerDate))}
          {infoField(t('offerDetail.validUntil'), formatDate(offer.validUntil))}
          {infoField(t('offerDetail.paymentTerms'), paymentTermsValue)}
          {infoField(
            t('offerDetail.totalValue'),
            <Text fz={14} fw={700} style={{ color: 'var(--color-text-primary)' }}>
              {formatAmount(offer.totalValue)}
            </Text>
          )}
          {acceptedTrailing && (
            <>
              <div style={{ height: 1, backgroundColor: 'var(--color-border-soft)' }} />
              {acceptedTrailing}
            </>
          )}
        </Stack>
      </Stack>
    </>
  );

  // Ad-hoc stavke (bez cjenika) se dodaju/uređuju kroz vlastitu formu sa zasebnim spremanjem —
  // s nespremljenim nacrtom prvo pitamo, da se dva načina spremanja ne miješaju.
  const openAddItems = () => {
    if (hasPriceList) {
      setAddItemModalOpen(true);
    } else {
      guard(() => {
        setEditingLine(null);
        setAddItemModalOpen(true);
      });
    }
  };

  const renderLineAction = (line: DisplayLine) => {
    if (line.isRemoved) return <UndoRemoveButton onClick={() => removeLine(line)} />;
    if (hasPriceList || line.isNew || !line.line) {
      return <RemoveLineButton onClick={() => removeLine(line)} />;
    }
    const savedLine = line.line;
    return (
      <LineActionsMenu
        onEdit={() => guard(() => openEditLineModal(savedLine))}
        onRemove={() => removeLine(line)}
      />
    );
  };

  const columns: [string, number | undefined, 'left' | 'right' | 'center'][] = [
    [t('offerDetail.colSortOrder'), 28, 'left'],
    [t('offerDetail.colCode'), TABLE_ITEM_CODE_COL_WIDTH, 'left'],
    [t('offerDetail.colDescription'), undefined, 'left'],
    [t('offerDetail.colCategory'), 100, 'left'],
    [t('offerDetail.colQuantity'), 70, 'right'],
    [t('offerDetail.colUnit'), 64, 'center'],
    [t('offerDetail.colPrice'), 100, 'right'],
    [t('offerDetail.colNormHours'), 80, 'right'],
    [t('offerDetail.colAmount'), 110, 'right'],
    ...(canEdit ? ([[t('createOffer.colRemove'), 60, 'right']] as [string, number, 'right'][]) : [])
  ];

  return (
    <Container size="lg" px={0}>
      <Stack gap={24}>
        <BackButton to="/offers" />

        {savedMessage && (
          <StatusBanner
            tone="success"
            message={savedMessage}
            onDismiss={() => setSavedMessage(null)}
          />
        )}

        {isAccepted && <StatusBanner tone="success" message={t('offerDetail.acceptedBanner')} />}

        {previewError && <Toast tone="danger">{t('offerDetail.previewError')}</Toast>}
        {deleteError && <Toast tone="danger">{deleteError}</Toast>}

        <Group justify="space-between" wrap="wrap" gap={12} visibleFrom="sm">
          <Group gap={12}>
            <Title order={2} fz={24} style={{ color: 'var(--color-text-primary)' }}>
              {t('offerDetail.title', { number: offerLabel })}
            </Title>
            <StatusPill label={t(config.labelKey)} bg={config.bg} fg={config.fg} header />
          </Group>
          <Group gap={12}>
            {canEdit && canApprove && (
              <MutationButton
                variant="outline"
                isPending={approveMutation.isPending}
                isPaused={approveMutation.isPaused}
                disabled={lines.length === 0}
                onClick={() => guard(() => id && approveMutation.mutate({ id }))}
              >
                {t('offerDetail.approveButton')}
              </MutationButton>
            )}
            {canReturnToDraft && (
              <MutationButton
                variant="outline"
                isPending={returnToDraftMutation.isPending}
                isPaused={returnToDraftMutation.isPaused}
                onClick={() => id && returnToDraftMutation.mutate({ id })}
              >
                {t('offerDetail.returnToDraftButton')}
              </MutationButton>
            )}
            {canSend && (
              <MutationButton
                isPending={sendMutation.isPending}
                isPaused={sendMutation.isPaused}
                onClick={() => setSendModalOpen(true)}
              >
                {t('offerDetail.sendButton')}
              </MutationButton>
            )}
            {canCreateContract &&
              // CON-09: Klijentova inicijativa (createFromOfferByClient) kreira NJEGOVU VLASTITU
              // kopiju Ugovora — vidljiva mu je isto kao Partnerova njemu, pa linkedContract radi
              // za obje strane (za razliku od normalnog toka gdje klijent ništa ne vidi dok
              // Partner ne pošalje).
              (linkedContract ? (
                <Button
                  variant="outline"
                  onClick={() => navigate(`/contracts/${linkedContract.id}`)}
                >
                  {t('offerDetail.viewContract')}
                </Button>
              ) : (
                <Button onClick={() => navigate(`/contracts/new?offerId=${offer.id}`)}>
                  {t('offerDetail.createContract')}
                </Button>
              ))}
            <MutationButton
              variant="filled"
              isPending={isPreviewLoading}
              onClick={() => guard(handlePreview)}
            >
              {t('offerDetail.previewButton')}
            </MutationButton>
            {canEdit && (
              <Button variant="outline" onClick={() => navigate(`/offers/${id}/edit`)}>
                {t('offerFormEdit.title')}
              </Button>
            )}
            {canDelete && (
              <Button
                color="red"
                variant="outline"
                onClick={() => guard(() => setDeleteModalOpen(true))}
              >
                {t('common.delete')}
              </Button>
            )}
            {canCancel && (
              <Button color="red" variant="outline" onClick={() => setCancelModalOpen(true)}>
                {t('offerDetail.cancel')}
              </Button>
            )}
            {canAct && (
              <>
                <Button color="red" variant="outline" onClick={() => setRejectModalOpen(true)}>
                  {t('offerDetail.reject')}
                </Button>
                <MutationButton
                  isPending={acceptMutation.isPending}
                  isPaused={acceptMutation.isPaused}
                  onClick={() => id && acceptMutation.mutate({ id })}
                >
                  {t('offerDetail.accept')}
                </MutationButton>
              </>
            )}
          </Group>
        </Group>
        <Stack gap={8} hiddenFrom="sm">
          <Title order={2} fz={22} style={{ color: 'var(--color-text-primary)' }}>
            {t('offerDetail.title', { number: offerLabel })}
          </Title>
          <StatusPill label={t(config.labelKey)} bg={config.bg} fg={config.fg} header />
          <MobileActionBar>
            {canEdit && canApprove && (
              <MutationButton
                variant="outline"
                isPending={approveMutation.isPending}
                isPaused={approveMutation.isPaused}
                disabled={lines.length === 0}
                onClick={() => guard(() => id && approveMutation.mutate({ id }))}
                fullWidth
              >
                {t('offerDetail.approveButton')}
              </MutationButton>
            )}
            {canReturnToDraft && (
              <MutationButton
                variant="outline"
                fullWidth
                isPending={returnToDraftMutation.isPending}
                isPaused={returnToDraftMutation.isPaused}
                onClick={() => id && returnToDraftMutation.mutate({ id })}
              >
                {t('offerDetail.returnToDraftButton')}
              </MutationButton>
            )}
            {canSend && (
              <MutationButton
                isPending={sendMutation.isPending}
                isPaused={sendMutation.isPaused}
                onClick={() => setSendModalOpen(true)}
                fullWidth
              >
                {t('offerDetail.sendButton')}
              </MutationButton>
            )}
            {canCreateContract &&
              (linkedContract ? (
                <Button
                  variant="outline"
                  fullWidth
                  onClick={() => navigate(`/contracts/${linkedContract.id}`)}
                >
                  {t('offerDetail.viewContract')}
                </Button>
              ) : (
                <Button fullWidth onClick={() => navigate(`/contracts/new?offerId=${offer.id}`)}>
                  {t('offerDetail.createContract')}
                </Button>
              ))}
            <MutationButton
              variant="filled"
              isPending={isPreviewLoading}
              onClick={() => guard(handlePreview)}
              fullWidth
            >
              {t('offerDetail.previewButton')}
            </MutationButton>
            {canEdit && (
              <Button variant="outline" fullWidth onClick={() => navigate(`/offers/${id}/edit`)}>
                {t('offerFormEdit.title')}
              </Button>
            )}
            {canDelete && (
              <Button
                color="red"
                variant="outline"
                fullWidth
                onClick={() => guard(() => setDeleteModalOpen(true))}
              >
                {t('common.delete')}
              </Button>
            )}
            {canCancel && (
              <Button
                color="red"
                variant="outline"
                fullWidth
                onClick={() => setCancelModalOpen(true)}
              >
                {t('offerDetail.cancel')}
              </Button>
            )}
            {canAct && (
              <Button
                color="red"
                variant="outline"
                fullWidth
                onClick={() => setRejectModalOpen(true)}
              >
                {t('offerDetail.reject')}
              </Button>
            )}
            {canAct && (
              <MutationButton
                isPending={acceptMutation.isPending}
                isPaused={acceptMutation.isPaused}
                onClick={() => id && acceptMutation.mutate({ id })}
                fullWidth
              >
                {t('offerDetail.accept')}
              </MutationButton>
            )}
          </MobileActionBar>
        </Stack>

        <StatusTracker steps={trackerSteps} />

        {generalInfoContent}

        {isRejected && (
          <Box
            p={16}
            style={{
              borderRadius: 12,
              border: '1px solid var(--color-status-danger)',
              backgroundColor: 'var(--color-status-danger-soft)'
            }}
          >
            <Stack gap={10}>
              <Text fz={13} fw={700} style={{ color: 'var(--color-status-danger)' }}>
                {t('offerDetail.rejectionDetailsTitle')}
              </Text>
              <Group gap={24} wrap="wrap">
                <Text fz={12.5} style={{ color: 'var(--color-text-secondary)' }}>
                  <Text component="span" fw={600} style={{ color: 'var(--color-text-primary)' }}>
                    {t('offerDetail.rejectedBy')}
                  </Text>{' '}
                  {offer.decidedByName ?? '—'}
                </Text>
                <Text fz={12.5} style={{ color: 'var(--color-text-secondary)' }}>
                  <Text component="span" fw={600} style={{ color: 'var(--color-text-primary)' }}>
                    {t('offerDetail.rejectedDateInline')}
                  </Text>{' '}
                  {formatDate(offer.updatedAt)}
                </Text>
              </Group>
              <Stack gap={4}>
                <Text
                  fz={11}
                  fw={600}
                  tt="uppercase"
                  style={{ color: 'var(--color-status-danger)', letterSpacing: 0.4 }}
                >
                  {t('offerDetail.rejectionReasonLabel')}
                </Text>
                <Text fz={13} style={{ color: 'var(--color-text-primary)' }}>
                  {offer.rejectionReason}
                </Text>
              </Stack>
            </Stack>
          </Box>
        )}

        {canEdit && (
          <>
            <Group justify="flex-end" gap={16} wrap="wrap" visibleFrom="sm">
              <ItemsSaveStatus summary={draftSummary} justSaved={justSaved} savedAt={savedAt} />
              <Button size="sm" variant="outline" onClick={openAddItems}>
                {t(hasPriceList ? 'createOffer.addItem' : 'offerLineForm.addItemButton')}
              </Button>
            </Group>
            <Group justify="space-between" gap={12} wrap="nowrap" hiddenFrom="sm">
              <ItemsSaveStatus summary={draftSummary} justSaved={justSaved} savedAt={savedAt} />
              <Button size="xs" variant="outline" onClick={openAddItems}>
                {t(hasPriceList ? 'createOffer.addItem' : 'offerLineForm.addItemButton')}
              </Button>
            </Group>
          </>
        )}

        {saveError && (
          <StatusBanner tone="danger" message={saveError} onDismiss={() => setSaveError(null)} />
        )}

        <Box visibleFrom="sm" style={{ overflowX: 'auto' }}>
          <CardSection>
            <div style={{ minWidth: 700 }}>
              <div
                className="table-header-row"
                style={{
                  ...tableRowStyle,
                  padding: '12px 20px',
                  backgroundColor: 'var(--color-bg-canvas)',
                  borderBottom: '1px solid var(--color-border-soft)'
                }}
              >
                {columns.map(([label, width, align]) => (
                  <Text
                    key={label}
                    fz={10}
                    fw={600}
                    ta={align}
                    style={{
                      color: 'var(--color-text-muted)',
                      letterSpacing: 0.4,
                      width,
                      flex: width ? undefined : 1
                    }}
                  >
                    {label}
                  </Text>
                ))}
              </div>

              {pageLines.map((line, index) => {
                const hasDraftState =
                  line.isNew ||
                  line.isRemoved ||
                  line.isChanged ||
                  !!line.quantityError ||
                  !!line.priceError;
                const mutedText = line.isRemoved ? 'var(--color-text-muted)' : undefined;
                return (
                  <div
                    key={line.key}
                    style={{
                      ...tableRowStyle,
                      padding: '12px 20px',
                      borderTop: index === 0 ? undefined : '1px solid var(--color-border-soft)',
                      backgroundColor: hasDraftState ? DRAFT_ROW_BACKGROUND : undefined
                    }}
                  >
                    <Text fz={12} style={{ color: 'var(--color-text-muted)', width: 28 }}>
                      {linesOffset + index + 1}
                    </Text>
                    <Text
                      fz={12}
                      style={{
                        color: mutedText ?? 'var(--color-text-primary)',
                        width: TABLE_ITEM_CODE_COL_WIDTH
                      }}
                    >
                      {line.itemCode ?? '—'}
                    </Text>
                    <Group gap={8} wrap="nowrap" justify="space-between" style={{ flex: 1 }}>
                      <Text
                        fz={13.5}
                        style={{
                          color: mutedText ?? 'var(--color-text-secondary)',
                          textDecoration: line.isRemoved ? 'line-through' : undefined
                        }}
                      >
                        {line.description}
                      </Text>
                      {line.isNew && <NewItemBadge />}
                    </Group>
                    <Text
                      fz={12}
                      style={{ color: mutedText ?? 'var(--color-text-secondary)', width: 100 }}
                    >
                      {line.category ?? '—'}
                    </Text>
                    <div style={{ width: 70, display: 'flex', justifyContent: 'flex-end' }}>
                      {canEdit ? (
                        <DraftNumberInput
                          width={70}
                          value={line.quantity}
                          savedValue={line.savedQuantity}
                          isNew={line.isNew}
                          error={line.quantityError}
                          disabled={line.isRemoved}
                          ariaLabel={t('offerDetail.colQuantity')}
                          onChange={(value) => setLineValue(line, 'quantity', value)}
                        />
                      ) : (
                        <Text fz={13.5} ta="right" style={{ color: 'var(--color-text-secondary)' }}>
                          {line.quantity}
                        </Text>
                      )}
                    </div>
                    <Text
                      fz={13.5}
                      ta="center"
                      style={{ color: mutedText ?? 'var(--color-text-secondary)', width: 64 }}
                    >
                      {findUnitLabel(unitsData, line.unitId, i18n.language)}
                    </Text>
                    <div style={{ width: 100, display: 'flex', justifyContent: 'flex-end' }}>
                      {canEdit ? (
                        <DraftNumberInput
                          width={100}
                          allowDecimal
                          decimalScale={2}
                          value={line.unitPrice}
                          savedValue={line.savedUnitPrice}
                          isNew={line.isNew}
                          error={line.priceError}
                          disabled={line.isRemoved}
                          formatSaved={formatNumber}
                          ariaLabel={t('offerDetail.colPrice')}
                          onChange={(value) => setLineValue(line, 'unitPrice', value)}
                        />
                      ) : (
                        <Text fz={13} style={{ color: 'var(--color-text-secondary)' }}>
                          {formatAmount(Number(line.unitPrice))}
                        </Text>
                      )}
                    </div>
                    <Text
                      fz={13.5}
                      ta="right"
                      style={{ color: mutedText ?? 'var(--color-text-secondary)', width: 80 }}
                    >
                      {formatNumber(line.normHours)}
                    </Text>
                    <Text
                      fz={13.5}
                      fw={600}
                      ta="right"
                      style={{ color: mutedText ?? 'var(--color-text-primary)', width: 110 }}
                    >
                      {formatAmount(lineAmount(line))}
                    </Text>
                    {canEdit && (
                      <Group w={60} justify="flex-end">
                        {renderLineAction(line)}
                      </Group>
                    )}
                  </div>
                );
              })}

              {displayLines.length === 0 && (
                <Center py={40}>
                  <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
                    {t('offerDetail.noLines')}
                  </Text>
                </Center>
              )}

              {canEdit && (
                <ItemsSaveBar
                  summary={draftSummary}
                  isSaving={saveLinesMutation.isPending}
                  onCancel={discardDraft}
                  onSave={saveDraft}
                />
              )}

              {displayLines.length > 0 && (
                <ListPagination
                  label={t('offerDetail.linesShowing', {
                    shown: pageLines.length,
                    total: displayLines.length
                  })}
                  page={linesPage}
                  totalPages={linesTotalPages}
                  onPageChange={setLinesPage}
                  pageSize={linesPageSize}
                  onPageSizeChange={setLinesPageSize}
                />
              )}
            </div>
          </CardSection>
        </Box>

        <Stack hiddenFrom="sm" gap={12}>
          {pageLines.map((line, index) => {
            const unitLabel = findUnitLabel(unitsData, line.unitId, i18n.language);
            const metaValue = (value: string) => (
              <Text span fz={11} fw={700} style={{ color: 'var(--color-text-primary)' }}>
                {value}
              </Text>
            );
            return (
              <Stack
                key={line.key}
                gap={10}
                p={16}
                style={{
                  borderRadius: 12,
                  border: '1px solid var(--color-border-soft)',
                  backgroundColor:
                    line.isNew ||
                    line.isRemoved ||
                    line.isChanged ||
                    !!line.quantityError ||
                    !!line.priceError
                      ? DRAFT_ROW_BACKGROUND
                      : 'var(--color-bg-surface)'
                }}
              >
                <Group justify="space-between" wrap="nowrap">
                  <Group gap={8} wrap="nowrap">
                    <Text fz={12.5} fw={700} style={{ color: 'var(--color-text-primary)' }}>
                      {t('offerDetail.cardTitle', {
                        number: linesOffset + index + 1,
                        code: line.itemCode ?? '—'
                      })}
                    </Text>
                    {line.isNew && <NewItemBadge />}
                  </Group>
                  {canEdit && renderLineAction(line)}
                </Group>
                <Text
                  fz={13.5}
                  style={{
                    color: line.isRemoved
                      ? 'var(--color-text-muted)'
                      : 'var(--color-text-secondary)',
                    textDecoration: line.isRemoved ? 'line-through' : undefined
                  }}
                >
                  {line.description}
                </Text>
                <Group gap={12} wrap="wrap" style={{ rowGap: 4 }}>
                  <Text fz={11} style={{ color: 'var(--color-text-muted)' }}>
                    {t('offerDetail.cardCategory')} {metaValue(line.category ?? '—')}
                  </Text>
                  <Text fz={11} style={{ color: 'var(--color-text-muted)' }}>
                    {t('offerDetail.cardNormHours')} {metaValue(formatNumber(line.normHours))}
                  </Text>
                  <Text fz={11} style={{ color: 'var(--color-text-muted)' }}>
                    {t('offerDetail.cardAmount')} {metaValue(formatAmount(lineAmount(line)))}
                  </Text>
                </Group>
                <Group justify="space-between" wrap="nowrap" align="flex-start">
                  <Text
                    fz={13}
                    pt={canEdit ? 10 : 0}
                    style={{ color: 'var(--color-text-secondary)' }}
                  >
                    {t('offerDetail.cardQuantity', { unit: unitLabel })}
                  </Text>
                  {canEdit ? (
                    <DraftNumberInput
                      width={80}
                      height={40}
                      value={line.quantity}
                      savedValue={line.savedQuantity}
                      isNew={line.isNew}
                      error={line.quantityError}
                      disabled={line.isRemoved}
                      ariaLabel={t('offerDetail.colQuantity')}
                      onChange={(value) => setLineValue(line, 'quantity', value)}
                    />
                  ) : (
                    <Text fz={13.5} fw={600} style={{ color: 'var(--color-text-primary)' }}>
                      {line.quantity}
                    </Text>
                  )}
                </Group>
                <Group justify="space-between" wrap="nowrap" align="flex-start">
                  <Text
                    fz={13}
                    pt={canEdit ? 10 : 0}
                    style={{ color: 'var(--color-text-secondary)' }}
                  >
                    {t('offerDetail.cardPrice')}
                  </Text>
                  {canEdit ? (
                    <DraftNumberInput
                      width={110}
                      height={40}
                      allowDecimal
                      decimalScale={2}
                      value={line.unitPrice}
                      savedValue={line.savedUnitPrice}
                      isNew={line.isNew}
                      error={line.priceError}
                      disabled={line.isRemoved}
                      formatSaved={formatNumber}
                      ariaLabel={t('offerDetail.colPrice')}
                      onChange={(value) => setLineValue(line, 'unitPrice', value)}
                    />
                  ) : (
                    <Text fz={13.5} fw={600} style={{ color: 'var(--color-text-primary)' }}>
                      {formatAmount(Number(line.unitPrice))}
                    </Text>
                  )}
                </Group>
              </Stack>
            );
          })}

          {displayLines.length === 0 && (
            <Center py={40}>
              <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
                {t('offerDetail.noLines')}
              </Text>
            </Center>
          )}

          {displayLines.length > 0 && (
            <ListPagination
              label={t('offerDetail.linesShowing', {
                shown: pageLines.length,
                total: displayLines.length
              })}
              page={linesPage}
              totalPages={linesTotalPages}
              onPageChange={setLinesPage}
              pageSize={linesPageSize}
              onPageSizeChange={setLinesPageSize}
            />
          )}
        </Stack>

        <Stack gap={16} align="flex-end" visibleFrom="sm">
          <Stack
            gap={12}
            p={20}
            style={{
              width: 340,
              maxWidth: '100%',
              borderRadius: 12,
              border: '1px solid var(--color-border-soft)',
              backgroundColor: 'var(--color-bg-surface)'
            }}
          >
            {showVat && (
              <Group justify="space-between">
                <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
                  {t('offerDetail.subtotal')}
                </Text>
                <Text fz={14} fw={500} style={{ color: 'var(--color-text-primary)' }}>
                  {formatAmount(subtotal)}
                </Text>
              </Group>
            )}
            {showVat && (
              <Group justify="space-between">
                <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
                  {t('offerDetail.vat', { rate: vatRate })}
                </Text>
                <Text fz={14} fw={500} style={{ color: 'var(--color-text-primary)' }}>
                  {formatAmount(vat)}
                </Text>
              </Group>
            )}
            {showVat && divider}
            <Group justify="space-between">
              <Text fz={14} fw={700} style={{ color: 'var(--color-text-primary)' }}>
                {t('offerDetail.total')}
              </Text>
              <Text fz={18} fw={700} style={{ color: 'var(--color-text-primary)' }}>
                {formatAmount(total)}
              </Text>
            </Group>
            {draftSummary.total > 0 && (
              <Text fz={11} style={{ color: 'var(--color-text-muted)' }}>
                {t('itemsDraft.includesUnsaved')}
              </Text>
            )}
          </Stack>
        </Stack>

        <Stack
          hiddenFrom="sm"
          gap={10}
          p={16}
          style={{
            borderRadius: 12,
            border: '1px solid var(--color-border-soft)',
            backgroundColor: 'var(--color-bg-surface)'
          }}
        >
          {showVat && (
            <Group justify="space-between">
              <Text fz={12.5} style={{ color: 'var(--color-text-muted)' }}>
                {t('offerDetail.subtotal')}
              </Text>
              <Text fz={13.5} fw={500} style={{ color: 'var(--color-text-primary)' }}>
                {formatAmount(subtotal)}
              </Text>
            </Group>
          )}
          {showVat && (
            <Group justify="space-between">
              <Text fz={12.5} style={{ color: 'var(--color-text-muted)' }}>
                {t('offerDetail.vat', { rate: vatRate })}
              </Text>
              <Text fz={13.5} fw={500} style={{ color: 'var(--color-text-primary)' }}>
                {formatAmount(vat)}
              </Text>
            </Group>
          )}
          {showVat && divider}
          <Group justify="space-between">
            <Text fz={14} fw={700} style={{ color: 'var(--color-text-primary)' }}>
              {t('offerDetail.total')}
            </Text>
            <Text fz={17} fw={700} style={{ color: 'var(--color-text-primary)' }}>
              {formatAmount(total)}
            </Text>
          </Group>
          {draftSummary.total > 0 && (
            <Text fz={11} style={{ color: 'var(--color-text-muted)' }}>
              {t('itemsDraft.includesUnsaved')}
            </Text>
          )}
        </Stack>

        {isRejected && (
          <StatusBanner
            tone="info"
            icon={<IconFigmaInfo size={16} />}
            message={t('offerDetail.rejectedNextSteps')}
          />
        )}
      </Stack>

      <RejectOfferModal
        opened={rejectModalOpen}
        offerLabel={offerLabel ?? ''}
        onClose={() => setRejectModalOpen(false)}
        onConfirm={(reason) => id && rejectMutation.mutate({ id, data: { reason } })}
        loading={rejectMutation.isPending}
        isPaused={rejectMutation.isPaused}
      />

      <ConfirmActionModal
        opened={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={() => id && deactivateMutation.mutate({ id })}
        title={t('offerDetail.deleteConfirmTitle')}
        message={t('offerDetail.deleteConfirmMessage', { number: offerLabel })}
        confirmLabel={t('common.delete')}
        danger
        loading={deactivateMutation.isPending}
        isPaused={deactivateMutation.isPaused}
      />

      <ConfirmActionModal
        opened={cancelModalOpen}
        onClose={() => setCancelModalOpen(false)}
        onConfirm={() => id && cancelMutation.mutate({ id })}
        title={t('offerDetail.cancelConfirmTitle')}
        message={t('offerDetail.cancelConfirmMessage', { number: offerLabel })}
        confirmLabel={t('offerDetail.cancel')}
        danger
        loading={cancelMutation.isPending}
        isPaused={cancelMutation.isPaused}
      />

      {canSend && id && (
        <SendOfferModal
          opened={sendModalOpen}
          offerLabel={offerLabel ?? ''}
          onClose={() => setSendModalOpen(false)}
          onConfirm={() => sendMutation.mutate({ id })}
          loading={sendMutation.isPending}
          isPaused={sendMutation.isPaused}
        />
      )}

      {canEdit && id && (
        <>
          {hasPriceList ? (
            <OfferItemPickerModal
              opened={addItemModalOpen}
              onClose={() => setAddItemModalOpen(false)}
              priceListId={offer.priceListId}
              priceListName={linkedPriceList?.name}
              existingPriceListItemIds={[
                ...lines.map((line) => line.priceListItemId),
                ...draft.added.map((item) => item.meta.id)
              ].filter((itemId): itemId is string => !!itemId)}
              onAdd={addPriceListItems}
            />
          ) : (
            <OfferLineFormModal
              opened={addItemModalOpen}
              onClose={closeLineModal}
              offerId={id}
              existingCategories={existingCategories}
              editingLine={editingLine}
              onSaved={setSavedMessage}
            />
          )}
        </>
      )}
      {canEdit && (
        <Box hiddenFrom="sm">
          <ItemsSaveBar
            variant="mobile"
            summary={draftSummary}
            isSaving={saveLinesMutation.isPending}
            onCancel={discardDraft}
            onSave={saveDraft}
          />
        </Box>
      )}

      {unsavedChangesModal}
    </Container>
  );
};
